import React, { useEffect } from "react";
import { FormInstance, Input } from "antd";
import { IDocumentType, IDrivers } from "store/lobby/lobby-model";
import { isDriverLicenseDocumentType, isRequiredDocumentType } from "utils/documentType";
import { driverValidator, validation, validationCPF, validationDriverLicense, validationSpecialCharacters } from "utils/form/validations";
import { validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./index.styled";

interface IDriversInput {
  form: FormInstance;
  disabledFields: boolean;
  drivers?: IDrivers[];
  documentType?: IDocumentType;
  isFieldDisabled?: (args) => boolean;
  setDisabledFieldsFilled?: React.Dispatch<React.SetStateAction<any>>;
  disabledFieldsFilled?: {
    [key: string]: boolean;
  };
}

const DriversInputs = ({ form, disabledFields, drivers, documentType, isFieldDisabled, setDisabledFieldsFilled, disabledFieldsFilled }: IDriversInput): JSX.Element => {
  const REQUIRED_FIELD = useTranslation({ id: "required_field" });
  const DRIVER = useTranslation({ id: "driver" });
  const DRIVER_LICENSE = useTranslation({ id: "driver_license" });
  const INVALID_DRIVER_LICENSE = useTranslation({ id: "invalid_driver_license" });
  const IDENTIFICATION_INITIALS = useTranslation({ id: "identification_initials" });
  const INVALID_IDENTIFICATION = useTranslation({ id: "invalid_identification" });
  const CPF_RESET = "___.___.___-__";

  const isDriverLicense = isDriverLicenseDocumentType(documentType);
  const isDocumentRequired = isRequiredDocumentType(documentType);
  const requiredLabel = (label: string) => `${label}${isDocumentRequired ? "*" : ""}`;

  const formatCpf = (value = "") => {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  };

  useEffect(() => {
    if (!setDisabledFieldsFilled) return;
    setTimeout(() => {
      const driver1Value = form.getFieldValue("driver1");
      const cpf1Value = form.getFieldValue("cpf1");
      const hasDriver1 = !!driver1Value && driver1Value.length > 0;
      const hasCpf1 = !!cpf1Value && (isDriverLicense ? validationDriverLicense.test(cpf1Value) : validationCPF.test(cpf1Value));

      setDisabledFieldsFilled({
        ...disabledFieldsFilled,
        driver1: !!hasDriver1,
        cpf1: !!hasCpf1,
      });
    }, 0);
  }, [form, isDriverLicense]);

  const identificationValidator = (value) => value !== CPF_RESET || value !== undefined || value !== null;
  const driverNameValidator = (value) => value !== "" || value !== undefined || value !== null;

  const driverFields = {
    driver1: ["driver1", "cpf1"],
  };

  const resetAndValidateFields = (form, fieldsToReset) => {
    fieldsToReset.forEach((field) => {
      form.resetFields([field]);
    });
    form.validateFields(fieldsToReset);
  };

  useEffect(() => {
    if (drivers && drivers.length > 0) {
      if (!drivers[0]) {
        resetAndValidateFields(form, driverFields.driver1);
      }
    } else if (drivers && drivers.length === 0) {
      resetAndValidateFields(form, [...driverFields.driver1]);
    }
  }, [drivers]);

  const resetEmptyFieldAndValidateDependencies = (fieldName: string, dependentFields: string[]) => (event) => {
    if (!event.target.value) {
      form.resetFields([fieldName]);
    }

    dependentFields.forEach((field) => form.validateFields([field]));
  };

  return (
    <div data-testid="driversContainer">
      <S.FormDrivers>
        <S.FormItem
          label={requiredLabel(DRIVER)}
          style={S.DriversField}
          name="driver1"
          hasFeedback={disabledFields ? false : driverNameValidator(form.getFieldValue("driver1"))}
          rules={[driverValidator(form, "driver1", "cpf1", CPF_RESET, REQUIRED_FIELD, isDocumentRequired)]}
        >
          <Input
            style={S.InputDriver}
            disabled={disabledFields || isFieldDisabled?.("driver1")}
            maxLength={150}
            onChange={resetEmptyFieldAndValidateDependencies("driver1", ["cpf1"])}
            data-testid="inputDriver1"
          />
        </S.FormItem>
        {isDriverLicense ? (
          <S.FormItem
            label={requiredLabel(DRIVER_LICENSE)}
            style={S.CpfField}
            name="cpf1"
            hasFeedback={disabledFields ? false : identificationValidator(form.getFieldValue("cpf1"))}
            rules={[
              {
                pattern: validationDriverLicense,
                message: INVALID_DRIVER_LICENSE,
              },
              driverValidator(form, "driver1", "cpf1", CPF_RESET, REQUIRED_FIELD, isDocumentRequired),
            ]}
          >
            <Input
              style={S.InputCpf}
              data-testid="inputCpf1"
              disabled={disabledFields || isFieldDisabled?.("cpf1")}
              maxLength={13}
              onChange={resetEmptyFieldAndValidateDependencies("cpf1", ["driver1"])}
              onKeyPress={(e) => validationCharacters(e, validationSpecialCharacters)}
            />
          </S.FormItem>
        ) : (
          <S.FormItem
            label={requiredLabel(IDENTIFICATION_INITIALS)}
            style={S.CpfField}
            name="cpf1"
            getValueFromEvent={(event) => formatCpf(event.target.value)}
            hasFeedback={disabledFields ? false : identificationValidator(form.getFieldValue("cpf1"))}
            rules={[
              {
                pattern: validationCPF,
                message: INVALID_IDENTIFICATION,
              },
              driverValidator(form, "driver1", "cpf1", CPF_RESET, REQUIRED_FIELD, isDocumentRequired),
            ]}
          >
            <Input
              style={S.InputCpf}
              data-testid="inputCpf1"
              disabled={disabledFields || isFieldDisabled?.("cpf1")}
              maxLength={14}
              placeholder={CPF_RESET}
              onChange={resetEmptyFieldAndValidateDependencies("cpf1", ["driver1"])}
              onKeyPress={(e) => validation(e)}
            />
          </S.FormItem>
        )}
      </S.FormDrivers>
    </div>
  );
};

export default DriversInputs;
